import React, { useState } from 'react';
import { useMultiStep } from '../hooks/useMultiStep';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { getImagesByStep } from '../utils/templateMapper';
import ZentorgaForm from '../components/ZentorgaForm';
import SurveyQuestion from '../components/SurveyQuestion';
import BenefitPeriod from '../../components/BenefitPeriod';
import KeepButton from '../components/KeepButton';
import SoundVideo from '../components/SoundVideo';
import '../styles/zentorga.scss';
import '../styles/template33.scss';

const Template33 = ({
  landingData,
  urlCode,
  urlCodeSettingId
}) => {
  const { currentStep, goToNextStep } = useMultiStep(3);
  const { handleSubmit, isSubmitting } = useFormSubmit(
    urlCode,
    urlCodeSettingId,
    landingData.endpoint
  );

  const [survey1Answer, setSurvey1Answer] = useState('');
  const [survey2Answer, setSurvey2Answer] = useState('');

  const stepImages = getImagesByStep(landingData, 3);

  const survey1Options = [
    landingData.surveyOpt1_1,
    landingData.surveyOpt1_2,
    landingData.surveyOpt1_3,
    landingData.surveyOpt1_4,
    landingData.surveyOpt1_5
  ].filter(Boolean);

  const survey2Options = [
    landingData.surveyOpt2_1,
    landingData.surveyOpt2_2,
    landingData.surveyOpt2_3,
    landingData.surveyOpt2_4,
    landingData.surveyOpt2_5
  ].filter(Boolean);

  const handleStep1Next = () => {
    goToNextStep();
  };

  const handleStep2Next = () => {
    goToNextStep({ survey1Answer, survey2Answer });
  };

  const handleFormSubmit = async (formData) => {
    await handleSubmit({
      ...formData,
      selectedOption: survey1Answer,
      selectedOption2: survey2Answer
    });
  };

  const renderImages = (images) => {
    if (!images || images.length === 0) return null;
    return (
      <div className="zentorga-images">
        {images.map((img, idx) => {
          const fileExtension = img.url.split('.').pop().toLowerCase();
          const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
          return isVideo ? (
            <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
          ) : (
            <img key={idx} src={img.url} alt={img.alt} />
          );
        })}
      </div>
    );
  };

  return (
    <section id="template33" className="template33-layout">
      {/* Step 1 */}
      {currentStep === 0 && (
        <div className="step step1-container">
          {renderImages(stepImages.step1)}
          <KeepButton
            onClick={handleStep1Next}
            imageUrl="https://d1k8m48tkgq306.cloudfront.net/images/eventiro/postBTN.png"
          />
        </div>
      )}

      {/* Step 2 */}
      {currentStep === 1 && (
        <div className="step step2-container">
          {renderImages(stepImages.step2)}

          <div className="surveySection">
            <SurveyQuestion
              topic={landingData.surveyTitle1}
              answers={survey1Options}
              onChange={setSurvey1Answer}
              questionNumber={1}
              displayType={landingData.surveyTopic?.topic?.[0]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            />
          </div>

          <div className="surveySection">
            <SurveyQuestion
              topic={landingData.surveyTitle2}
              answers={survey2Options}
              onChange={setSurvey2Answer}
              questionNumber={2}
              displayType={landingData.surveyTopic?.topic?.[1]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            />
          </div>

          <KeepButton
            onClick={handleStep2Next}
            imageUrl="https://d1k8m48tkgq306.cloudfront.net/images/eventiro/postBTN.png"
          />
        </div>
      )}

      {/* Step 3 */}
      {currentStep === 2 && (
        <div className="step step3-container">
          {renderImages(stepImages.step3)}

          {landingData.eventTarget && (
            <div className="eventTarget">
              <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
            </div>
          )}

          {landingData.benefitDate && (
            <div className="benefitDate">
              <p>혜택 기간 : <BenefitPeriod periodType={landingData.benefitDate} /></p>
            </div>
          )}

          <ZentorgaForm
            onSubmit={handleFormSubmit}
            formStyle={landingData.formStyleClassName || 'gray'}
            consentStyle={landingData.privacyPolicyClassName || 'dark'}
            privacyPolicyName={landingData.privacyPolicyName || '본원'}
            privacyPolicy={landingData.privacyPolicy}
            marketingPolicy={landingData.marketingPolicy}
            buttonComponent={
              <button
                type="submit"
                form="zentorga-form"
                id="postBtn"
                className={`postBtn ${landingData.postBtnLocate === 'fixed' ? 'postBtn-fixed' : ''}`}
                disabled={isSubmitting}
              >
                {landingData.postBtnUrl ? (
                  <img src={landingData.postBtnUrl} alt="신청하기" />
                ) : (
                  <span>{isSubmitting ? '신청 중...' : '신청하기'}</span>
                )}
              </button>
            }
          />

          {landingData.serviceInfoClassName && (
            <p
              className={`serviceInfoClassName ${landingData.serviceInfoClassName}`}
              id="serviceInfoClassName"
            >
              * 본 서비스는 전국 20여 개 이상의 제휴 병원에서 제공되며,<br />
              상담 신청 정보를 기반으로 전문 상담원이 적합한 제휴 병원을 안내해드립니다.
            </p>
          )}

          {landingData.footerClassName !== null && (landingData.footerClassName !== undefined || landingData.footerDetail) && (
            <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
              {landingData.footerDetail && (
                <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
                  {landingData.footerDetail}
                </p>
              )}
            </footer>
          )}
        </div>
      )}
    </section>
  );
};

export default Template33;
